import React from 'react';
import Modal from './Modal';
import Button from './Button';
import { useI18n } from '../../context/I18nContext';
import { Sparkles, AlertTriangle } from 'lucide-react';

export interface UpgradePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Modal title (e.g. "Fonctionnalité Pro" or "Limite atteinte") */
  title: string;
  /** Main message (e.g. "Passe à Pro pour utiliser la réécriture IA") */
  message: string;
  /** Optional subtitle (e.g. "3 / 10" or "2.5 MB / 100 MB" for quota) */
  subtitle?: string;
  /** Called when user clicks the upgrade button (caller should close and navigate to /pricing) */
  onUpgrade: () => void;
  /** Optional secondary action (e.g. archive a project to free a slot) */
  secondaryAction?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
    icon?: React.ReactNode;
  };
}

/**
 * Shared modal for "upgrade to Pro" prompts: quota limit reached or Pro-only feature.
 * Modern, consistent design used across the app.
 */
const UpgradePromptModal: React.FC<UpgradePromptModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  subtitle,
  onUpgrade,
  secondaryAction,
}) => {
  const { t } = useI18n();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <div className="flex flex-col gap-2 w-full">
          {secondaryAction && (
            <Button
              onClick={secondaryAction.onClick}
              variant="outline"
              className="w-full rounded-xl whitespace-nowrap"
              loading={secondaryAction.loading}
              icon={secondaryAction.icon}
            >
              {secondaryAction.label}
            </Button>
          )}
          <Button
            onClick={onUpgrade}
            variant="primary"
            className="w-full rounded-xl shadow-md hover:shadow-lg transition-shadow"
            icon={<Sparkles className="w-4 h-4" />}
            disabled={secondaryAction?.loading}
          >
            {t('nav.tryPro')}
          </Button>
        </div>
      }
    >
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-50 text-amber-600 ring-1 ring-amber-200/60">
            <AlertTriangle className="h-5 w-5" aria-hidden />
          </span>
        </div>
        <p className="text-base text-gray-700 leading-relaxed mb-2">{message}</p>
        {subtitle && (
          <p className="text-sm text-gray-500">{subtitle}</p>
        )}
      </div>
    </Modal>
  );
};

export default UpgradePromptModal;
